import { useRef, useState, useEffect } from "react"
import { MediaType } from "../../types/mainModulTypes"
import { API } from "../../constants/constants"
import { ReactComponent as Pause } from "../../assets/material-symbols_pause-rounded.svg"
import { ReactComponent as Play } from "../../assets/material-symbols_play-arrow-rounded.svg"

type BaseMediaType = {
    media: MediaType
    classProps?: string
}

export default function BaseMedia(props: BaseMediaType) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [showButton, setShowButton] = useState(true)
    const [canAutoplay, setCanAutoplay] = useState(true)

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause()
            } else {
                const playPromise = videoRef.current.play()
                if (playPromise !== undefined) {
                    playPromise.catch(() => {
                        // Autoplay failed, but that's okay
                        setCanAutoplay(false)
                    })
                }
            }
            setIsPlaying(!isPlaying)
        }
    }

    // Handle autoplay failures gracefully
    useEffect(() => {
        if (videoRef.current && props.media?.isVideo) {
            const video = videoRef.current

            // Listen for autoplay success/failure
            const handleCanPlay = () => {
                // Video is ready, check if it's playing
                if (!video.paused) {
                    setIsPlaying(true)
                    setShowButton(false)
                }
            }

            const handlePlay = () => {
                setIsPlaying(true)
                setShowButton(false)
            }

            const handlePause = () => {
                setIsPlaying(false)
                setShowButton(true)
            }

            video.addEventListener("canplay", handleCanPlay)
            video.addEventListener("play", handlePlay)
            video.addEventListener("pause", handlePause)

            return () => {
                video.removeEventListener("canplay", handleCanPlay)
                video.removeEventListener("play", handlePlay)
                video.removeEventListener("pause", handlePause)
            }
        }
    }, [props.media?.isVideo])

    if (props?.media.isImage) {
        return <img src={API + props.media?.url} alt={props.media?.description || props.media?.name} className={props.classProps} loading="lazy" />
    }

    if (props.media?.isVideo) {
        // Debug logging to help identify the issue
        console.log("Video Debug Info:", {
            url: API + props.media?.url,
            mimeType: props.media?.mimeType,
            fullMedia: props.media,
        })

        return (
            <div
                className="relative w-fit mx-auto group"
                onMouseEnter={() => isPlaying && setShowButton(true)}
                onMouseLeave={() => isPlaying && setShowButton(false)}>
                <video
                    ref={videoRef}
                    onPlay={() => {
                        setIsPlaying(true)
                        setShowButton(false)
                    }}
                    onPause={() => {
                        setIsPlaying(false)
                        setShowButton(true)
                    }}
                    onError={(e) => {
                        console.error("Video error:", e)
                        console.error("Video error details:", {
                            error: e.currentTarget.error,
                            networkState: e.currentTarget.networkState,
                            readyState: e.currentTarget.readyState,
                        })
                    }}
                    onLoadStart={() => console.log("Video load started")}
                    onLoadedData={() => console.log("Video data loaded")}
                    onCanPlay={() => console.log("Video can play")}
                    autoPlay={canAutoplay}
                    controls={false}
                    loop
                    muted
                    playsInline
                    loading="lazy"
                    {...({ "webkit-playsinline": "true" } as any)}
                    className={`${props?.classProps}`}>
                    <source src={API + props.media?.url} type={props.media?.mimeType} />
                    Your browser does not support the video tag.
                </video>

                {(showButton || !isPlaying) && (
                    <button
                        onClick={togglePlay}
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all">
                        {isPlaying ? <Pause className="w-24 md:w-40 text-white" /> : <Play className="w-24 md:w-40 text-white" />}
                    </button>
                )}
            </div>
        )
    }

    return null
}
